import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import DeleteModal from "components/common/DeleteModal";
import { useEmployeeOverview } from "../EmployeeOverview/EmployeeOverviewContext";
import type { EmployeeBankAccount } from "../types/employee-bank-account.types";
import {
  deleteEmployeeBankAccount,
  listEmployeeBankAccounts,
} from "../services/employeeBankAccountService";
import BankAccountFormModal from "./BankAccountFormModal";

const BankAccountsTab = () => {
  const { employee } = useEmployeeOverview();
  const employeeId = employee?.id;

  const [items, setItems] = useState<EmployeeBankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<EmployeeBankAccount | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<EmployeeBankAccount | null>(
    null,
  );

  const load = useCallback(async () => {
    if (!employeeId) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await listEmployeeBankAccounts(employeeId);
      if (res.success) {
        setItems(res.data ?? []);
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to load bank accounts",
      );
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (row: EmployeeBankAccount) => {
    setEditing(row);
    setFormOpen(true);
  };

  const confirmDelete = (row: EmployeeBankAccount) => {
    setDeleteTarget(row);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!employeeId || !deleteTarget) return;
    try {
      const res = await deleteEmployeeBankAccount(employeeId, deleteTarget.id);
      if (res.success) {
        toast.success(res.message);
        setDeleteOpen(false);
        setDeleteTarget(null);
        load();
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ?? "Failed to delete bank account",
      );
    }
  };

  if (!employeeId) {
    return (
      <Row>
        <Col>
          <Card>
            <CardBody className="text-muted">Employee not found.</CardBody>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteOpen}
        onCloseClick={() => {
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
        onDeleteClick={handleDelete}
        recordId={deleteTarget?.bankName}
      />

      <BankAccountFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        employeeId={employeeId}
        editRecord={editing}
        onSaved={load}
      />

      <Row>
        <Col>
          <Card>
            <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div>
                <h5 className="card-title mb-0">Bank details</h5>
                <p className="text-muted mb-0 small">
                  Payroll accounts for this employee
                </p>
              </div>
              <Button color="primary" size="sm" onClick={openCreate}>
                <i className="ri-add-line align-bottom me-1" />
                Add account
              </Button>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                </div>
              ) : items.length === 0 ? (
                <p className="text-muted mb-0">
                  No bank accounts on file. Add an account to store payout
                  details.
                </p>
              ) : (
                <div className="table-responsive table-card mb-3">
                  <Table className="table-nowrap align-middle mb-0">
                    <thead className="table-light text-muted">
                      <tr>
                        <th>Holder</th>
                        <th>Bank</th>
                        <th>Branch</th>
                        <th>Account no.</th>
                        <th>IFSC</th>
                        <th className="text-center">Primary</th>
                        <th className="text-end" style={{ width: "120px" }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((row) => (
                        <tr key={row.id}>
                          <td className="fw-medium">{row.accountHolderName}</td>
                          <td>{row.bankName}</td>
                          <td>{row.branchName || "—"}</td>
                          <td>
                            <span className="font-monospace">
                              {row.accountNumber}
                            </span>
                          </td>
                          <td>
                            <span className="font-monospace text-uppercase">
                              {row.ifscCode}
                            </span>
                          </td>
                          <td className="text-center">
                            {row.isPrimary ? (
                              <span className="badge bg-primary-subtle text-primary">
                                Primary
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="text-end">
                            <div className="hstack gap-1 justify-content-end">
                              <Button
                                color="light"
                                size="sm"
                                className="btn-soft-primary"
                                onClick={() => openEdit(row)}
                              >
                                <i className="ri-pencil-line" />
                              </Button>
                              <Button
                                color="light"
                                size="sm"
                                className="btn-soft-danger"
                                onClick={() => confirmDelete(row)}
                              >
                                <i className="ri-delete-bin-line" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default BankAccountsTab;
